// quill-setup.ts

// eslint-disable-next-line max-classes-per-file
import Quill from 'quill';

/* --------------------------------------------------
   1) Importer tout ce dont on a besoin pour Quill
-----------------------------------------------------*/
const DirectionAttribute = Quill.import('attributors/attribute/direction');
const AlignStyle = Quill.import('attributors/style/align');
const BackgroundStyle = Quill.import('attributors/style/background');
const ColorStyle = Quill.import('attributors/style/color');
const DirectionStyle = Quill.import('attributors/style/direction');
const FontStyle = Quill.import('attributors/style/font');
const SizeStyle = Quill.import('attributors/style/size');
const Bold = Quill.import('formats/bold');
const Parchment = Quill.import('parchment');
const Break = Quill.import('blots/break');
const Block = Quill.import('blots/block');
const Inline = Quill.import('blots/inline');
const Icons = Quill.import('ui/icons');
Icons.align.left = Icons.align['']; // set icon for 'left' option, otherwise it's replaced with 'undefined' text
/* --------------------------------------------------
   2) Personnaliser si besoin (whitelist, tagName, etc.)
-----------------------------------------------------*/

// Ex : autoriser align: left + version custom pour la toolbar
AlignStyle.whitelist = ['left', 'center', 'right', 'justify'];

// Forcer l'utilisation de <b> à la place de <strong>
Bold.tagName = 'B';

// Autoriser certaines polices
FontStyle.whitelist = [
  '',
  'arial, helvetica, sans-serif',
  'comic sans ms, cursive',
  'courier new, courier, monospace',
  'georgia, serif',
  'lucida sans unicode, lucida grande, sans-serif',
  'tahoma, geneva, sans-serif',
  'times new roman, times, serif',
  'trebuchet ms, helvetica, sans-serif',
  'verdana, geneva, sans-serif',
];

// Autoriser certaines tailles
SizeStyle.whitelist = [
  '',
  '8px', '9px', '10px', '11px', '12px',
  '13px', '14px', '15px', '16px', '18px',
  '20px', '22px', '24px', '26px', '28px',
  '30px', '32px', '34px', '36px', '38px',
  '40px', '42px', '44px', '46px', '48px',
  '50px', '52px', '54px', '56px', '58px',
  '60px',
];

/* --------------------------------------------------
   3) Configurer line-height et letter-spacing
-----------------------------------------------------*/
const lineHeightConfig = {
  scope: Parchment.Scope.BLOCK, // BLOCK pour que le line-height s'applique sur le <p>, pas le <span>
  whitelist: null, // tu peux mettre un tableau si besoin
};

const lineHeightAttributor = new Parchment.Attributor.Style('lineheight', 'line-height', lineHeightConfig);

const letterSpacingAttributor = new Parchment.Attributor.Style('letterspacing', 'letter-spacing', {
  scope: Parchment.Scope.INLINE,
});

/* --------------------------------------------------
   4) Redéfinir/étendre certains blots de Quill
-----------------------------------------------------*/

// A) Break custom (pour éviter la suppression de <br>)
class FixedBreak extends Break {
  insertInto(parent: any, ref: any) {
    Parchment.Embed.prototype.insertInto.call(this, parent, ref);
  }

  // eslint-disable-next-line class-methods-use-this
  length() {
    return 1;
  }

  // eslint-disable-next-line class-methods-use-this
  value() {
    return '\n';
  }
}

// B) Block custom (conserve les attributs sur <p> par ex.)
class FixedBlock extends Block {
  static create(value: any) {
    const node = super.create(value);
    // eslint-disable-next-line guard-for-in,no-restricted-syntax
    for (const key in value) {
      node.setAttribute(key, value[key]);
    }
    return node;
  }

  static formats(domNode: any) {
    const attrs: Record<string, any> = {};
    // eslint-disable-next-line no-restricted-syntax
    for (const attr of domNode.attributes) {
      attrs[attr.name] = attr.value;
    }
    return attrs;
  }
}
FixedBlock.allowedChildren.push(FixedBreak);
FixedBlock.blotName = 'fixed_block';

/* --------------------------------------------------
   5) Blot inline custom (mais NON enregistré par défaut)
      On le définit seulement, pour l'activer conditionnellement
-----------------------------------------------------*/
class FixedInline extends Inline {
  static create(value: any) {
    const node = super.create(value);
    // eslint-disable-next-line guard-for-in,no-restricted-syntax
    for (const key in value) {
      node.setAttribute(key, value[key]);
    }
    return node;
  }

  static formats(domNode: any) {
    const attrs: Record<string, any> = {};
    // eslint-disable-next-line no-restricted-syntax
    for (const attr of domNode.attributes) {
      attrs[attr.name] = attr.value;
    }
    return attrs;
  }
}
FixedInline.blotName = 'fixed_inline';

/* --------------------------------------------------
   5b) Custom Link Blot — preserve data-redirect attribute
       for funnel redirect links (#redirect_XXX)
-----------------------------------------------------*/
const QuillLink = Quill.import('formats/link');

class CustomLink extends QuillLink {
  static create(value: string) {
    const node = super.create(value);
    if (typeof value === 'string') {
      CustomLink.applySpmAttributes(node, value);
    }
    return node;
  }

  static applySpmAttributes(node: HTMLElement, value: string) {
    // Preserve the raw href against browser URL normalization
    // (drag & drop rewrites href to absolute, mangling {var=...} variables)
    node.setAttribute('data-spm-href', value);
    // Extract the redirect ID (supports both #redirect_XXX and #spin_redirect_XXX)
    const parts = value.includes('#spin_redirect_')
      ? value.split('#spin_redirect_')
      : value.split('#redirect_');
    const redirectId = parts[1] || '';
    if (redirectId) {
      node.setAttribute('data-redirect', redirectId);
    } else {
      node.removeAttribute('data-redirect');
    }
  }

  // Quill's Link.format only rewrites href on a URL change
  format(name: string, value: unknown) {
    super.format(name, value);
    if (name === CustomLink.blotName && typeof value === 'string' && value) {
      CustomLink.applySpmAttributes(this.domNode, value);
    }
  }

  static formats(domNode: HTMLElement) {
    return domNode.getAttribute('data-spm-href') || domNode.getAttribute('href') || '';
  }

  static sanitize(url: string) {
    if (typeof url === 'string' && url.includes('{var=')) return url;
    return super.sanitize(url);
  }
}
CustomLink.blotName = 'link';
CustomLink.tagName = 'A';

/* --------------------------------------------------
   6) Enregistrer globalement tout ce qui DOIT l'être
      (sauf le FixedInline si on veut l'activer conditionnellement)
-----------------------------------------------------*/
Quill.register(DirectionAttribute, true);
Quill.register(AlignStyle, true);
Quill.register(BackgroundStyle, true);
Quill.register(ColorStyle, true);
Quill.register(DirectionStyle, true);
Quill.register(FontStyle, true);
Quill.register(SizeStyle, true);
Quill.register(Bold, true);
Quill.register(CustomLink, true);
Quill.register(lineHeightAttributor, true);
Quill.register(letterSpacingAttributor, true);

/* --------------------------------------------------
   7) Exporter une fonction d'enregistrement conditionnel
      pour FixedInline
-----------------------------------------------------*/
let isImportTemplateSpecialsRegistered = false;
export function registerImportTemplateSpecialsRegistering() {
  if (!isImportTemplateSpecialsRegistered) {
    Quill.register(FixedInline, true);
    Quill.register(FixedBreak, true);
    Quill.register(FixedBlock, true);
    isImportTemplateSpecialsRegistered = true;
  }
}

/* --------------------------------------------------
   8) Appliquer le résultat du dialogue de lien
-----------------------------------------------------*/
export function applyLinkToRange(
  quill: any,
  range: { index: number; length: number },
  url: string,
  text: string,
  fallbackText: string,
) {
  const { index, length } = range;
  const typedText = text.trim() ? text : '';
  const originalText = length > 0 ? quill.getText(index, length) : '';
  // An image link has no text: a blank text field keeps its content
  const hasEmbed = length > 0
    && quill.getContents(index, length).ops.some((op: { insert: unknown }) => typeof op.insert !== 'string');

  if (length > 0 && (typedText ? typedText === originalText : hasEmbed)) {
    quill.formatText(index, length, 'link', url, 'silent');
    return;
  }
  // Preserve inline formats (color, bold, size, etc.) common to the original selection
  const preservedFormats = length > 0 ? quill.getFormat(index, length) : {};
  if (length > 0) quill.deleteText(index, length, 'silent');
  quill.insertText(index, typedText || fallbackText, { ...preservedFormats, link: url }, 'silent');
}

/* --------------------------------------------------
   9) Exporter Quill configuré par défaut
-----------------------------------------------------*/
export { FontStyle };
export default Quill;
